import React, { useState, useEffect, useLayoutEffect, useContext, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import AlertModal from '../components/AlertModal';
import ConfirmModal from '../components/ConfirmModal';
import { User as UserType, AlertState } from '../types';
import { getUserPhotoUrl } from '../utils/userPhoto';
import PremiumSelect from '../components/PremiumSelect';
import './Users.css';

interface UserFormData {
  name: string;
  email: string;
  password: string;
  /** No cadastro: administrador, RH ou funcionário (API: `developer`). */
  role: 'admin' | 'hr' | 'developer';
  active: boolean;
}

interface ConfirmDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => Promise<void>;
}

const Users: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { user, refreshUser } = authContext || {};
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [alert, setAlert] = useState<AlertState>({ isOpen: false, message: '', type: 'error' });
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState<boolean>(false);
  const [isSavingUser, setIsSavingUser] = useState<boolean>(false);
  const [formData, setFormData] = useState<UserFormData>({
    name: '',
    email: '',
    password: '',
    role: 'developer',
    active: true,
  });
  const formSectionRef = useRef<HTMLElement | null>(null);
  /** Incrementado ao abrir o formulário; força novo scroll mesmo com Strict Mode ou re-render. */
  const [formScrollNonce, setFormScrollNonce] = useState<number>(0);

  const loadUsers = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<UserType[]>('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'hr')) {
      loadUsers();
    }
  }, [loadUsers, user]);

  useLayoutEffect(() => {
    if (!showForm) {
      return;
    }

    let cancelled = false;

    const scrollToForm = (): void => {
      if (cancelled) {
        return;
      }
      const el = formSectionRef.current;
      if (!el) {
        return;
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    /** Dois RAFs garantem medição/layout após pintar selects e o painel premium. */
    const rafId = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(scrollToForm);
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(rafId);
    };
  }, [showForm, formScrollNonce]);

  const resetForm = useCallback((): void => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'developer',
      active: true,
    });
    setEditingUserId(null);
    setPhotoFile(null);
    setPhotoPreview(null);
  }, []);

  const openCreateForm = (): void => {
    if (showForm && !editingUserId) {
      setShowForm(false);
      resetForm();
      return;
    }

    resetForm();
    setFormScrollNonce((n) => n + 1);
    setShowForm(true);
  };

  const openEditForm = (selectedUser: UserType): void => {
    setFormScrollNonce((n) => n + 1);
    setEditingUserId(selectedUser.id);
    const formRole: UserFormData['role'] =
      selectedUser.role === 'admin'
        ? 'admin'
        : selectedUser.role === 'hr'
          ? 'hr'
          : 'developer';
    setFormData({
      name: selectedUser.name,
      email: selectedUser.email,
      password: '',
      role: formRole,
      active: selectedUser.active,
    });
    setPhotoFile(null);
    setPhotoPreview(getUserPhotoUrl(selectedUser));
    setShowForm(true);
  };

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];

    if (!file) {
      setPhotoFile(null);
      setPhotoPreview(editingUserId ? photoPreview : null);
      return;
    }

    if (photoPreview && photoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(photoPreview);
    }

    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const currentEditingUserId = editingUserId;

    const payload = new FormData();
    payload.append('name', formData.name);
    payload.append('email', formData.email);
    payload.append('role', formData.role);
    payload.append('active', String(formData.active));

    if (formData.password) {
      payload.append('password', formData.password);
    }

    if (photoFile) {
      payload.append('photo', photoFile);
    }

    if (!currentEditingUserId && !formData.password) {
      setAlert({
        isOpen: true,
        message: 'Informe uma senha para criar o usuário.',
        type: 'warning',
      });
      return;
    }

    setIsSavingUser(true);
    try {
      if (currentEditingUserId) {
        await api.patch(`/users/${currentEditingUserId}`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post('/users', payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setShowForm(false);
      resetForm();
      await loadUsers();

      if (currentEditingUserId === user?.id && refreshUser) {
        await refreshUser();
      }

      setAlert({
        isOpen: true,
        message: currentEditingUserId
          ? 'Usuário atualizado com sucesso!'
          : 'Usuário criado com sucesso!',
        type: 'success',
      });
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message:
          error.response?.data?.message ||
          (currentEditingUserId ? 'Erro ao atualizar usuário' : 'Erro ao criar usuário'),
        type: 'error',
      });
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleDelete = async (selectedUser: UserType): Promise<void> => {
    if (selectedUser.id === user?.id) {
      setAlert({
        isOpen: true,
        message: 'Você não pode excluir o próprio usuário logado.',
        type: 'warning',
      });
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: 'Excluir usuário',
      message: `Deseja realmente excluir ${selectedUser.name}?`,
      onConfirm: async () => {
        try {
          await api.delete(`/users/${selectedUser.id}`);
          await loadUsers();
          setAlert({
            isOpen: true,
            message: 'Usuário excluído com sucesso!',
            type: 'success',
          });
        } catch (error: any) {
          setAlert({
            isOpen: true,
            message: error.response?.data?.message || 'Erro ao excluir usuário',
            type: 'error',
          });
        }
      },
    });
  };

  const closeConfirmDialog = (): void => {
    if (isConfirmingDelete) return;
    setConfirmDialog(null);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!confirmDialog) return;
    setIsConfirmingDelete(true);
    try {
      await confirmDialog.onConfirm();
      setConfirmDialog(null);
    } finally {
      setIsConfirmingDelete(false);
    }
  };

  const managers = useMemo(() => users.filter((u) => u.role === 'manager'), [users]);
  const roleSelectOptions = useMemo(
    () => [
      { value: 'admin', label: 'Administrador' },
      { value: 'hr', label: 'RH' },
      { value: 'developer', label: 'Funcionário' },
    ],
    [],
  );
  const userActiveOptions = useMemo(
    () => [
      { value: 'true', label: 'Ativo' },
      { value: 'false', label: 'Inativo' },
    ],
    [],
  );
  const totalActiveUsers = useMemo(() => users.filter((u) => u.active).length, [users]);
  const totalManagers = managers.length;
  const totalDevelopers = useMemo(
    () => users.filter((u) => u.role === 'developer').length,
    [users]
  );

  if (!user || (user.role !== 'admin' && user.role !== 'hr')) {
    return <div className="container">Acesso negado</div>;
  }

  if (loading) {
    return (
      <div className="users-state">
        <div className="users-state-card">
          <span className="users-badge">Carregando</span>
          <h1>Preparando usuários</h1>
          <p>Organizando perfis, permissões e relacionamentos da equipe.</p>
        </div>
      </div>
    );
  }

  const roleLabel = (role: UserType['role']): string => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'manager':
        return 'Gestor';
      case 'developer':
        return 'Funcionário';
      case 'hr':
        return 'RH';
      default:
        return role;
    }
  };

  const roleClass = (role: UserType['role']): string => {
    return `user-role-pill role-${role}`;
  };

  const levelLabel = (level?: UserType['level']): string => {
    if (!level) return 'Não definido';
    switch (level) {
      case 'junior':
        return 'Júnior';
      case 'pleno':
        return 'Pleno';
      case 'senior':
        return 'Sênior';
      default:
        return level;
    }
  };

  const statusClass = (active: boolean): string =>
    `user-status-pill ${active ? 'is-active' : 'is-inactive'}`;

  const getInitials = (name: string): string =>
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('');

  return (
    <div className="users-page">
      <section className="users-hero">
        <div className="users-hero-copy">
          <span className="users-badge">Gestão de acessos</span>
          <h1>Usuários e permissões</h1>
          <p>
            Centralize a criação de perfis, organize papéis da equipe e mantenha a estrutura da
            plataforma sempre atualizada.
          </p>
        </div>

        <div className="users-hero-actions">
          <button
            onClick={openCreateForm}
            className={`users-action-button${showForm ? ' is-secondary' : ''}`}
          >
            {showForm && !editingUserId ? 'Fechar formulário' : 'Novo usuário'}
          </button>
        </div>
      </section>

      <section className="users-stats-grid">
        <article className="users-stat-card">
          <span>Total de usuários</span>
          <strong>{users.length}</strong>
          <p>Todos os perfis cadastrados no sistema.</p>
        </article>

        <article className="users-stat-card">
          <span>Usuários ativos</span>
          <strong>{totalActiveUsers}</strong>
          <p>Perfis disponíveis para uso e autenticação.</p>
        </article>

        <article className="users-stat-card">
          <span>Gestores</span>
          <strong>{totalManagers}</strong>
          <p>Responsáveis por acompanhar o time.</p>
        </article>

        <article className="users-stat-card">
          <span>Funcionário</span>
          <strong>{totalDevelopers}</strong>
          <p>Colaboradores com perfil operacional na plataforma.</p>
        </article>
      </section>

      {showForm && (
        <section
          ref={formSectionRef}
          className="users-surface users-form-surface users-form-surface--premium"
        >
          <div className="users-section-header users-form-section-header">
            <div>
              <span className="users-section-kicker">Cadastro</span>
              <h2>{editingUserId ? 'Editar usuário' : 'Criar novo usuário'}</h2>
            </div>
            <p>
              {editingUserId
                ? 'Atualize os dados do perfil, permissões e foto do usuário.'
                : 'Preencha os dados abaixo para liberar acesso ao sistema.'}
            </p>
          </div>

          <form className="users-form users-form--premium" onSubmit={handleSubmit}>
            <div className="users-form-grid">
              <div className="form-group">
                <label htmlFor="user-name">Nome *</label>
                <input
                  id="user-name"
                  className="premium-text-input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nome completo"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="user-email">Email *</label>
                <input
                  id="user-email"
                  className="premium-text-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="seu.email@empresa.com"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="user-password">{editingUserId ? 'Nova senha' : 'Senha *'}</label>
                <input
                  id="user-password"
                  className="premium-text-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={
                    editingUserId
                      ? 'Deixe em branco para manter a atual'
                      : 'Defina uma senha segura'
                  }
                  required={!editingUserId}
                  autoComplete="new-password"
                />
              </div>
              <PremiumSelect
                label="Perfil *"
                wrapperClassName="premium-select-field"
                value={formData.role}
                options={roleSelectOptions}
                onChange={(value) =>
                  setFormData({ ...formData, role: value as UserFormData['role'] })
                }
                placeholder="Selecione o perfil"
                required
              />
            </div>

            <div className="users-form-grid">
              <PremiumSelect
                label="Status"
                wrapperClassName="premium-select-field"
                value={formData.active ? 'true' : 'false'}
                options={userActiveOptions}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    active: value === 'true',
                  })
                }
              />

              <div className="form-group">
                <label htmlFor="user-photo">Foto do usuário</label>
                <input
                  id="user-photo"
                  className="premium-file-input"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                />
              </div>
            </div>

            <div className="users-photo-preview-card users-photo-preview-card--premium">
              {photoPreview ? (
                <img src={photoPreview} alt="Pré-visualização do usuário" className="users-photo-preview-image" />
              ) : (
                <div className="users-photo-preview-placeholder" aria-hidden="true">
                  Foto
                </div>
              )}
              <div className="users-photo-preview-copy">
                <strong>Pré-visualização</strong>
                <span>Envie uma imagem para exibir no header do sistema e nos cards de usuários.</span>
              </div>
            </div>

            <div className="users-form-footer users-form-footer--premium">
              <button
                type="button"
                className="users-form-footer-btn users-form-footer-btn--ghost"
                disabled={isSavingUser}
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className={`users-form-footer-btn users-form-footer-btn--primary${isSavingUser ? ' is-loading' : ''}`}
                disabled={isSavingUser}
                aria-busy={isSavingUser}
              >
                {isSavingUser ? (
                  <>
                    <span className="users-form-btn-spinner" aria-hidden />
                    {editingUserId ? 'Salvando…' : 'Criando…'}
                  </>
                ) : editingUserId ? (
                  'Salvar alterações'
                ) : (
                  'Criar usuário'
                )}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="users-surface">
        <div className="users-section-header">
          <div>
            <span className="users-section-kicker">Equipe</span>
            <h2>Perfis cadastrados</h2>
          </div>
          <p>{users.length} usuário(s) encontrado(s)</p>
        </div>

        <div className="users-grid">
          {users.map((u) => (
            <article className="user-card" key={u.id}>
              <div className="user-card-header">
                {getUserPhotoUrl(u) ? (
                  <img
                    src={getUserPhotoUrl(u) || ''}
                    alt={u.name}
                    className="user-avatar-image"
                  />
                ) : (
                  <div className="user-avatar" aria-hidden="true">
                    {getInitials(u.name)}
                  </div>
                )}

                <div className="user-card-headline">
                  <strong>{u.name}</strong>
                  <span>{u.email}</span>
                </div>
              </div>

              <div className="user-card-body">
                <div className="user-chip-row">
                  <span className={roleClass(u.role)}>{roleLabel(u.role)}</span>
                  <span className={statusClass(u.active)}>{u.active ? 'Ativo' : 'Inativo'}</span>
                </div>

                <dl className="user-meta-grid">
                  <div>
                    <dt>Nível</dt>
                    <dd>{levelLabel(u.level)}</dd>
                  </div>
                  <div>
                    <dt>Criado em</dt>
                    <dd>{new Date(u.createdAt).toLocaleDateString('pt-BR')}</dd>
                  </div>
                  <div>
                    <dt>Gestor</dt>
                    <dd>{u.manager?.name || 'Não definido'}</dd>
                  </div>
                </dl>

                <div className="user-card-actions">
                  <button
                    type="button"
                    className="user-card-button"
                    onClick={() => openEditForm(u)}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    className="user-card-button is-danger"
                    onClick={() => handleDelete(u)}
                  >
                    Excluir
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        message={alert.message}
        type={alert.type}
      />
      <ConfirmModal
        isOpen={Boolean(confirmDialog?.isOpen)}
        title={confirmDialog?.title}
        message={confirmDialog?.message || ''}
        confirmLabel="Excluir"
        isLoading={isConfirmingDelete}
        onClose={closeConfirmDialog}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default Users;
